import React, { useState, useMemo } from 'react';
import { Eye, Code, Mail } from 'lucide-react';

/**
 * Prepara el contenido HTML para que se visualice correctamente en un iframe.
 * Procesa marcadores de resaltado dinámico ([[RESALTAR]]...[[FIN]]) para que se vean
 * bonitos en el cuerpo del correo sin romper etiquetas o atributos HTML como href o src.
 */
function procesarHtmlEmail(htmlRaw) {
    if (!htmlRaw || typeof htmlRaw !== 'string') return '';

    let html = htmlRaw;

    // 1. Limpiar marcadores dentro de etiquetas o atributos (ej: href="[[RESALTAR]]url[[FIN]]")
    // para que los atributos HTML sigan siendo sintácticamente válidos.
    html = html.replace(/<[^>]*>/g, (tag) => {
        return tag.replace(/\[\[RESALTAR\]\](.*?)\[\[FIN\]\]/g, '$1');
    });

    // 2. En el contenido visible de texto, reemplazar los marcadores por un resalte elegante
    html = html.replace(
        /\[\[RESALTAR\]\](.*?)\[\[FIN\]\]/g,
        '<span style="background-color: #fef08a; color: #854d0e; padding: 1px 5px; border-radius: 3px; font-weight: bold; border: 1px solid #fde047;">$1</span>'
    );

    // 3. Comprobar si ya es un documento HTML completo
    const tieneEstructuraHtml = /<html|<!DOCTYPE|<body/i.test(html);
    if (tieneEstructuraHtml) {
        return html;
    }

    // 4. Si es un fragmento HTML o texto plano, envolver en un esqueleto estándar de email
    const esFragmentoHtml = /<[a-z][\s\S]*>/i.test(html);
    const contenidoCuerpo = esFragmentoHtml ? html : html.replace(/\n/g, '<br/>');

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: #334155;
      margin: 16px;
      background-color: #ffffff;
      -webkit-font-smoothing: antialiased;
    }
    img {
      max-width: 100%;
      height: auto;
      display: inline-block;
    }
    table {
      border-collapse: collapse;
      max-width: 100%;
    }
    a {
      color: #2563eb;
    }
  </style>
</head>
<body>
  ${contenidoCuerpo}
</body>
</html>`;
}

/**
 * Componente para previsualizar plantillas y avisos de Email.
 * Muestra el diseño real (imágenes, tablas, estilos, botones) usando un iframe aislado
 * en lugar de volcar el código fuente HTML en texto plano.
 */
const EmailPreview = ({
    mensaje = '',
    asunto = '',
    className = '',
    altura = 'h-[360px]',
    mostrarToggle = true
}) => {
    const [modoVista, setModoVista] = useState('visual'); // 'visual' | 'codigo'

    const htmlRenderizado = useMemo(() => {
        return procesarHtmlEmail(mensaje);
    }, [mensaje]);

    const esVacio = !mensaje || !mensaje.trim();

    return (
        <div className={`flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm ${className}`}>
            {/* Barra superior estilo cliente de correo */}
            <div className="bg-gray-100 border-b border-gray-200 px-3 py-2 flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-1.5 text-gray-700 truncate flex-1 min-w-0">
                    <Mail size={14} className="text-primary-600 flex-shrink-0" />
                    <span className="font-semibold text-gray-500 flex-shrink-0">Asunto:</span>
                    <span className="font-medium text-gray-900 truncate">
                        {asunto || <span className="text-gray-400 italic">(Sin asunto)</span>}
                    </span>
                </div>

                {mostrarToggle && !esVacio && (
                    <div className="flex items-center bg-gray-200 p-0.5 rounded border border-gray-300 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setModoVista('visual')}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                                modoVista === 'visual'
                                    ? 'bg-white text-primary-700 shadow-sm font-bold'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                            title="Ver diseño con imágenes y estilos reales"
                        >
                            <Eye size={12} />
                            Visual
                        </button>
                        <button
                            type="button"
                            onClick={() => setModoVista('codigo')}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                                modoVista === 'codigo'
                                    ? 'bg-white text-primary-700 shadow-sm font-bold'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                            title="Ver código fuente HTML"
                        >
                            <Code size={12} />
                            HTML
                        </button>
                    </div>
                )}
            </div>

            {/* Contenedor del contenido */}
            <div className={`relative w-full ${altura} bg-white overflow-hidden`}>
                {esVacio ? (
                    <div className="flex items-center justify-center h-full text-gray-400 text-xs italic p-4 text-center">
                        Empiece a escribir el cuerpo del correo para ver la vista previa en tiempo real...
                    </div>
                ) : modoVista === 'visual' ? (
                    <iframe
                        title="Vista previa del correo"
                        srcDoc={htmlRenderizado}
                        className="w-full h-full border-0 bg-white"
                        sandbox="allow-same-origin allow-popups"
                    />
                ) : (
                    <div className="p-3 h-full overflow-y-auto bg-gray-900 text-gray-200 font-mono text-xs whitespace-pre-wrap leading-relaxed select-all">
                        {mensaje}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailPreview;
